/**
 * Foundation Portal Layout + FoundationPortalAuthGuard
 *
 * Dedicated chrome for authenticated foundation/funder pages — mirrors the
 * Partner Portal pattern (PartnerPortalLayout). The foundation portal is a
 * SEPARATE org context (sponsor identity from a superuser picker or a direct
 * foundation login), so — exactly like partner — it owns its own auth store
 * (useFoundationAuthStore) and its own guard, and it reads neither
 * authService.getType() nor the shared agenticed_admin_auth blob.
 *
 * WHY THIS EXISTS: the foundation routes used to be guarded by
 * <AuthGuard requiredType="foundation">, which reads authService.getType() —
 * a function that can only ever return district/staff/vendor/null, never
 * 'foundation'. To force that shared guard to pass, foundation entry overwrote
 * agenticed_admin_auth with {type:'foundation'}, which DESTROYED the admin
 * superuser session and bounced every other portal (/vendor, /staff, /admin)
 * into /foundation. A store-backed guard removes the only reason that overwrite
 * existed, so the superuser's admin session is left intact.
 *
 * SUPERUSER RETURN: when an admin enters via the picker, agenticed_admin_auth
 * is preserved (isSuperuser:true). This layout surfaces a "Back to Admin"
 * control gated on that — so an uber-admin can roam into the foundation portal
 * and back out, the same way partner/vendor allow.
 */

import React, { useEffect, useState, ReactNode } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  useFoundationAuthStore,
  isFoundationAuthenticated,
} from '../../../../shared/stores/foundationAuthStore';

// ─────────────────────────────────────────────────────────────
// NAV CONFIG (ported from Layout.jsx FOUNDATION_NAV_ITEMS)
// ─────────────────────────────────────────────────────────────

const FOUNDATION_NAV = [
  { path: '/foundation',             label: 'Dashboard',   icon: '⬡' },
  { path: '/foundation/initiatives', label: 'Initiatives', icon: '◈' },
  { path: '/foundation/programs',    label: 'Programs',    icon: '⊞' },
  { path: '/foundation/recipients',  label: 'Recipients',  icon: '○' },
  { path: '/foundation/compliance',  label: '§25F Config', icon: '§' },
  { path: '/foundation/disbursement', label: '90/10 Panel', icon: '◧' },
  { path: '/foundation/codes',       label: 'Codes',       icon: '#' },
  { path: '/foundation/team',        label: 'Team',        icon: '≡' },
  { path: '/foundation/settings',    label: 'Settings',    icon: '⚙' },
];

// Read-only peek at the PRESERVED admin session. If an admin entered the
// foundation portal via the superuser picker, agenticed_admin_auth is left
// intact (we no longer overwrite it). Its presence + isSuperuser is what
// licenses the "Back to Admin" return — and `type` tells us which portal the
// admin came from so we land them back where they were.
function getSuperuserReturn(): { backType: string } | null {
  try {
    const raw = localStorage.getItem('agenticed_admin_auth');
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (s?.isSuperuser !== true) return null;
    const backType = ['staff', 'district', 'vendor'].includes(s.type) ? s.type : 'staff';
    return { backType };
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────
// AUTH GUARD
// ─────────────────────────────────────────────────────────────

export function FoundationPortalAuthGuard({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  // Subscribe to token so the guard re-evaluates when the session changes.
  const token = useFoundationAuthStore((s) => s.token);

  useEffect(() => {
    if (!isFoundationAuthenticated()) {
      navigate('/foundation/login', { replace: true });
    }
  }, [token, navigate]);

  if (!isFoundationAuthenticated()) return null;
  return <>{children}</>;
}

// ─────────────────────────────────────────────────────────────
// LAYOUT
// ─────────────────────────────────────────────────────────────

interface FoundationPortalLayoutProps {
  children: ReactNode;
  title?: string;
}

export default function FoundationPortalLayout({ children, title }: FoundationPortalLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useFoundationAuthStore((s) => s.user);
  const logout = useFoundationAuthStore((s) => s.logout);
  // Snapshot the superuser-return signal once on mount (the admin blob does not
  // change while inside the foundation portal).
  const [superReturn] = useState(getSuperuserReturn);

  const handleLogout = () => {
    logout();
    navigate('/foundation/login');
  };

  const handleBackToAdmin = () => {
    // Leaving the foundation portal: drop the minted foundation_session so a
    // later re-entry goes back through the picker fresh (no stale auto-enter).
    // The admin session was preserved, so the admin portal is reachable
    // directly via a full-page load.
    logout();
    window.location.assign(`/admin/${superReturn?.backType || 'staff'}`);
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Sidebar */}
      <aside className="w-56 bg-slate-900 flex flex-col shrink-0">
        {/* Logo */}
        <div className="px-5 py-5 border-b border-slate-700">
          <p className="text-amber-400 font-bold text-sm">AgenticED</p>
          <p className="text-slate-400 text-xs mt-0.5">Foundation Portal</p>
        </div>

        {/* Sponsor name */}
        {user?.sponsorName && (
          <div className="px-5 py-3 border-b border-slate-700">
            <p className="text-white text-xs font-semibold truncate">{user.sponsorName}</p>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-3 py-3 overflow-y-auto">
          {FOUNDATION_NAV.map((item) => {
            const isActive = item.path === '/foundation'
              ? location.pathname === '/foundation'
              : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm mb-0.5 transition-colors ${
                  isActive
                    ? 'bg-amber-600 text-white font-medium'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                <span className="text-xs w-4 text-center">{item.icon}</span>
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Footer — superuser return + user + sign out */}
        <div className="px-5 py-4 border-t border-slate-700 space-y-2">
          {superReturn && (
            <button
              onClick={handleBackToAdmin}
              className="block w-full text-left text-xs text-amber-400 hover:text-amber-300 transition-colors"
            >
              ← Back to Admin
            </button>
          )}
          <p className="text-xs text-slate-400 truncate">{user?.email || user?.sponsorName}</p>
          <button
            onClick={handleLogout}
            className="block text-xs text-slate-500 hover:text-slate-300 transition-colors"
          >
            Sign out
          </button>
          <button
            onClick={() => { logout(); window.location.assign('/admin/fsf'); }}
            className="block text-xs text-slate-500 hover:text-slate-300 transition-colors"
            title="Return to the demo persona picker"
          >
            ← Exit to Login
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        {title && (
          <div className="bg-white border-b border-slate-200 px-6 py-4">
            <h1 className="text-lg font-semibold text-slate-900">{title}</h1>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
